import { toast } from 'react-toastify';
import StyledHeadingText from '../../components/Fonts/StyledHeadingText';
import { getOrderDetails, patchOrder, postReturnCancellationLog } from '../../services/orders';
import OrderOrdersPopView from './OrderOrdersPopView';
import { useCallback, useEffect, useRef, useState } from 'react';
import OrderRewardPointPopView from './OrderRewardPointPopView';
import { BASE_URL } from '../../constants/apis';
import { getOrderLabel, getB2bOrderLabelQuick, generateB2bOrderLabel } from '../../services/labelInvoice';
import { BiCopy, BiPlus } from 'react-icons/bi';
import { getUserRole } from '../../helpers/local_storage_helpers';
import ShowModal from '../../utils/ShowModal';
import { ProductSearchInput } from './LabelInvoiceGenerator';

const statusColors = {
    PENDING: 'bg-yellow-100 text-yellow-700',
    CONFIRMED: 'bg-blue-100 text-blue-700',
    IN_PACKAGING: 'bg-purple-100 text-purple-700',
    DISPATCHED: 'bg-orange-100 text-orange-700',
    DELIVERED: 'bg-green-100 text-green-700',
    READY_TO_DISPATCH: 'bg-cyan-100 text-cyan-700',
    CANCELLED: 'bg-red-100 text-red-700',
    RETURNED: 'bg-gray-100 text-gray-600',
};

function InfoRow({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex flex-col gap-0.5">
            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">{label}</span>
            <span className="text-sm font-semibold text-gray-800">{value}</span>
        </div>
    );
}

function SectionCard({ title, children }) {
    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{title}</span>
            </div>
            <div className="p-4">{children}</div>
        </div>
    );
}

/** Resolve sales person label from various order-detail API shapes. */
function getSalesPersonDisplay(order) {
    if (!order) return null;
    if (order.sales_person_name) return order.sales_person_name;
    if (order.assigned_sales_person_name) return order.assigned_sales_person_name;
    if (order.staff_name) return order.staff_name;
    const sp = order.sales_person;
    if (sp == null || sp === '') return null;
    if (typeof sp === 'object') {
        return sp.name || sp.username || sp.email || null;
    }
    return String(sp);
}

const formatDisplayDate = (rawDate) => {
    if (!rawDate) return "";
    const value = String(rawDate).split("T")[0];
    const parts = value.split("-");
    if (parts.length === 3) {
        return `${parts[2]}/${parts[1]}/${parts[0]}`;
    }
    return value;
};

const formatCurrencyValue = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "0";
    return num % 1 === 0 ? String(num) : num.toFixed(2);
};

const formatOrderItemLine = (orderItem) => {
    const quantity = orderItem?.quantity ?? 1;
    const name =
        orderItem?.shop_product_aka_name ||
        orderItem?.aka_name ||
        orderItem?.product_name ||
        orderItem?.name ||
        orderItem?.product ||
        "Item";
    const quantityNumber = Number(quantity) || 1;
    const unitPrice = Number(orderItem?.price ?? orderItem?.unit_price ?? 0) ||
        (Number(orderItem?.total_price ?? 0) / quantityNumber);
    const linePrice = Number(orderItem?.total_price ?? 0) || unitPrice * quantityNumber;
    const isFree = Number.isFinite(linePrice) && linePrice <= 0;
    const priceText = Number.isFinite(unitPrice) ? ` - ₹${formatCurrencyValue(unitPrice)}/unit` : "";
    return `${quantity}x ${name}${priceText}${isFree ? " FREE" : ""}`;
};

const renderWhatsappPreviewLine = (line, index) => {
    const noteMatch = line.match(/^\*(Note - .*)\*$/);
    return (
        <div key={`${index}-${line}`} className={noteMatch ? "font-bold" : undefined}>
            {noteMatch ? noteMatch[1] : line || "\u00A0"}
        </div>
    );
};

const buildWhatsappPreviewMessage = (orderDetails, shipping, salesPersonLabel) => {
    const orderDate = formatDisplayDate(orderDetails?.order_date);
    const customerName = orderDetails?.user || orderDetails?.name || "Customer";
    const phone = String(
        orderDetails?.phone_number ||
        orderDetails?.number ||
        shipping?.phone_number ||
        orderDetails?.shipping_user?.phone_number ||
        ""
    ).replace(/[^\d]/g, "");
    const alternatePhone = String(
        orderDetails?.alternate_phone ||
        shipping?.alternate_phone ||
        orderDetails?.shipping_user?.alternate_phone ||
        ""
    ).replace(/[^\d]/g, "");
    const contactLine = (phone && alternatePhone && alternatePhone !== phone)
        ? `${phone}, ${alternatePhone}`
        : (phone || alternatePhone || "-");
    const totalAmount = Number(orderDetails?.total_price || 0);
    const remainingAmount = Number(orderDetails?.remaining_payment || 0);
    const paidAmount = Math.max(0, totalAmount - remainingAmount);
    const discount = Number(orderDetails?.discount || orderDetails?.discount_amount || 0);
    const deliveryCharge = Number(orderDetails?.delivery_charge || orderDetails?.delivery_charges || 0);
    const items = Array.isArray(orderDetails?.order_items) ? orderDetails.order_items : [];
    const productLines = items.length
        ? items.map(formatOrderItemLine)
        : ["Product details not available"];

    const transportName = String(shipping?.transport_name || "").trim();
    const transportNumber = String(shipping?.transport_number || "").trim();

    const noteAndTransportLines = [];
    if (transportName) noteAndTransportLines.push(`Transport - ${transportName}`);
    if (transportNumber) noteAndTransportLines.push(`Transport No. - ${transportNumber}`);

    const addressLines = [];
    if (shipping?.shipping_address) addressLines.push(`Address - ${shipping.shipping_address}`);
    if (shipping?.village) addressLines.push(`Village - ${shipping.village}`);
    if (shipping?.post_office) addressLines.push(`Post Office : ${shipping.post_office}`);
    if (shipping?.district) addressLines.push(`Dist. - ${shipping.district}`);
    if (shipping?.state) addressLines.push(`State - ${shipping.state}`);
    if (shipping?.zipcode) addressLines.push(`Pin Code - ${shipping.zipcode}`);

    const divider = "———————————";
    return [
        `Date: ${orderDate || "-"}`,
        `Name - ${customerName}`,
        ...(addressLines.length ? addressLines : ["Address - -"]),
        divider,
        `MOB. - ${contactLine}`,
        divider,
        "Product Details -",
        ...productLines,
        divider,
        ...(noteAndTransportLines.length ? [...noteAndTransportLines, divider] : []),
        `Total Amount: ₹${formatCurrencyValue(totalAmount)}/-`,
        `Discount - ₹${formatCurrencyValue(discount)}/-`,
        `Delivery Charges - ₹${formatCurrencyValue(deliveryCharge)}/-`,
        divider,
        `Paid Amount - ₹${formatCurrencyValue(paidAmount)}/-`,
        `Remaining Amount - ₹${formatCurrencyValue(remainingAmount)}`,
        divider,
        `Sales Person: ${salesPersonLabel || "-"}`,
    ].join("\n");
};

const REFUND_STATUS_OPTIONS = [
    { value: 'NOT_APPLICABLE', label: 'Not Applicable' },
    { value: 'PENDING', label: 'Pending' },
    { value: 'PROCESSED', label: 'Processed' },
    { value: 'REJECTED', label: 'Rejected' },
];

const refundStatusBadge = (status) => {
    const map = {
        NOT_APPLICABLE: 'bg-gray-100 text-gray-500',
        PENDING: 'bg-yellow-100 text-yellow-700',
        PROCESSED: 'bg-green-100 text-green-700',
        REJECTED: 'bg-red-100 text-red-700',
    };
    return map[status] || 'bg-gray-100 text-gray-500';
};

function ReturnCancellationSection({ orderId, logs, onAdded, canAdd }) {
    const [showForm, setShowForm] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const fileRef = useRef(null);
    const [form, setForm] = useState({
        reason: '', note: '', refund_amount: '',
        refund_status: '', refund_reference: '', file: null,
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.reason.trim()) { toast.error('Reason is required'); return; }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append('order', orderId);
            fd.append('reason', form.reason.trim());
            if (form.note.trim()) fd.append('note', form.note.trim());
            if (form.refund_amount !== '') fd.append('refund_amount', form.refund_amount);
            if (form.refund_status) fd.append('refund_status', form.refund_status);
            if (form.refund_reference.trim()) fd.append('refund_reference', form.refund_reference.trim());
            if (form.file) fd.append('file', form.file);
            const res = await postReturnCancellationLog(fd);
            if (res.status === 201) {
                toast.success('Log added');
                setForm({ reason: '', note: '', refund_amount: '', refund_status: '', refund_reference: '', file: null });
                if (fileRef.current) fileRef.current.value = '';
                setShowForm(false);
                onAdded?.();
            } else {
                toast.error('Failed to add log');
            }
        } catch {
            toast.error('Failed to add log');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="px-4 py-2.5 bg-gray-50 border-b border-gray-100 flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Return / Cancellation Logs</span>
                {canAdd && (
                    <button
                        type="button"
                        onClick={() => setShowForm((v) => !v)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-slate-800 text-white text-xs font-medium hover:bg-black transition-colors"
                    >
                        <BiPlus size={14} /> Add
                    </button>
                )}
            </div>

            {showForm && (
                <form onSubmit={handleSubmit} className="p-3 border-b border-gray-100 bg-gray-50">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                        <div className="sm:col-span-2">
                            <label className="block text-gray-500 mb-0.5">Reason *</label>
                            <input
                                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400"
                                value={form.reason}
                                onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                                placeholder="e.g. Customer returned damaged product"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label className="block text-gray-500 mb-0.5">Note</label>
                            <textarea
                                rows={2}
                                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400 resize-none"
                                value={form.note}
                                onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                                placeholder="Additional details..."
                            />
                        </div>
                        <div>
                            <label className="block text-gray-500 mb-0.5">Refund Amount</label>
                            <input
                                type="number" min="0" step="0.01"
                                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400"
                                value={form.refund_amount}
                                onChange={(e) => setForm((f) => ({ ...f, refund_amount: e.target.value }))}
                            />
                        </div>
                        <div>
                            <label className="block text-gray-500 mb-0.5">Refund Status</label>
                            <select
                                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400 bg-white"
                                value={form.refund_status}
                                onChange={(e) => setForm((f) => ({ ...f, refund_status: e.target.value }))}
                            >
                                <option value="">— Select —</option>
                                {REFUND_STATUS_OPTIONS.map((o) => (
                                    <option key={o.value} value={o.value}>{o.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-500 mb-0.5">Refund Reference (UTR/Txn)</label>
                            <input
                                className="w-full border border-gray-200 rounded-lg px-2.5 py-1.5 text-sm focus:outline-none focus:border-slate-400"
                                value={form.refund_reference}
                                onChange={(e) => setForm((f) => ({ ...f, refund_reference: e.target.value }))}
                                placeholder="UTR / transaction ID"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-500 mb-0.5">File / Proof</label>
                            <input
                                ref={fileRef}
                                type="file"
                                className="w-full text-xs text-gray-600 file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-slate-100 file:text-slate-700 hover:file:bg-slate-200"
                                onChange={(e) => setForm((f) => ({ ...f, file: e.target.files[0] || null }))}
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-2 mt-2.5">
                        <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1 rounded-lg border border-gray-200 text-xs text-gray-600 hover:bg-gray-100">Cancel</button>
                        <button type="submit" disabled={submitting} className="px-3 py-1 rounded-lg bg-slate-800 text-white text-xs font-medium hover:bg-black disabled:opacity-50">
                            {submitting ? 'Saving…' : 'Save'}
                        </button>
                    </div>
                </form>
            )}

            <div className="divide-y divide-gray-50">
                {!logs?.length ? (
                    <p className="px-4 py-3 text-xs text-gray-400">No return/cancellation logs.</p>
                ) : (
                    logs.map((log) => (
                        <div key={log.id} className="px-4 py-2.5 flex flex-wrap items-start gap-x-4 gap-y-1">
                            <div className="flex-1 min-w-0">
                                <p className="text-xs font-semibold text-gray-800 truncate">{log.reason}</p>
                                {log.note && <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{log.note}</p>}
                            </div>
                            <div className="flex flex-wrap items-center gap-2 shrink-0">
                                {parseFloat(log.refund_amount) > 0 && (
                                    <span className="text-xs font-semibold text-emerald-700">₹{parseFloat(log.refund_amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                                )}
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold ${refundStatusBadge(log.refund_status)}`}>
                                    {log.refund_status?.replace('_', ' ')}
                                </span>
                                {log.refund_reference && (
                                    <span className="text-[10px] text-gray-400 font-mono">{log.refund_reference}</span>
                                )}
                                {log.file && (
                                    <a href={log.file} target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 underline">File</a>
                                )}
                                {log.staff && <span className="text-[10px] text-gray-400">by {log.staff}</span>}
                                <span className="text-[10px] text-gray-300">{log.created_at?.split('T')[0]}</span>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}

function OrderOrdersDetailsPopView({ item, order_id, onListRefresh }) {
    const [orderDetails, setOrderDetails] = useState(item || {});
    const [isLoading, setIsLoading] = useState(false);
    const [loadingLabel, setLoadingLabel] = useState({ prepaid: false, cod: false });
    const [loadingB2bLabel, setLoadingB2bLabel] = useState(false);
    const [isEditorOpen, setIsEditorOpen] = useState(false);
    const [editingOrder, setEditingOrder] = useState({
        customer_name: '',
        shop_name: '',
        mobile_number: '',
        alternate_number: '',
        transport_name: '',
        transport_number: '',
        note: '',
        dispatch_instruction: '',
        items: [{ name: '', quantity: 1, note: '' }]
    });
    const [showPreview, setShowPreview] = useState(false);
    const [editTotalAmount, setEditTotalAmount] = useState({ editing: false, value: '', saving: false });
    const isAdmin = getUserRole() === 'admin';

    const calculateUsedPoints = () => {
        if (!orderDetails?.reward_points || !Array.isArray(orderDetails.reward_points)) return 0;
        return orderDetails.reward_points
            .filter(rp => rp.type === 'used')
            .reduce((sum, rp) => sum + (rp.points || 0), 0);
    };

    const totalUsedPoints = calculateUsedPoints();

    const getOrderDisplayNumber = (ord) =>
        ord?.invoice_id ||
        ord?.invoice_number ||
        ord?.invoice_no ||
        ord?.order_number ||
        ord?.display_id ||
        ord?.order_id ||
        ord?.id;

    const copyUuid = (uuid) => {
        if (!uuid) return;
        navigator.clipboard.writeText(uuid)
            .then(() => toast.success('UUID copied to clipboard!'))
            .catch(() => toast.error('Failed to copy UUID'));
    };

    useEffect(() => {
        if (order_id && (!item)) {
            fetchOrderDetails();
        } else {
            setOrderDetails(item);
        }
    }, [order_id, item]);

    async function fetchOrderDetails() {
        setIsLoading(true);
        try {
            const response = await getOrderDetails(order_id);
            if (response.status === 200) {
                setOrderDetails(response.data);
            } else {
                toast.error("Unable to fetch orders");
            }
        } catch (error) {
            console.error('Error fetching order details:', error);
        } finally {
            setIsLoading(false);
        }
    }

    const reloadOrderDetails = useCallback(async () => {
        const oid = orderDetails?.id || order_id;
        if (!oid) return;
        try {
            const response = await getOrderDetails(oid);
            if (response.status === 200) {
                setOrderDetails(response.data);
                onListRefresh?.();
            }
        } catch (error) {
            console.error('Error refreshing order:', error);
            toast.error('Unable to refresh order');
        }
    }, [orderDetails?.id, order_id, onListRefresh]);

    const handlePrintLabel = async (labelType) => {
        const oid = orderDetails?.id || order_id;
        if (!oid) { toast.error('No order ID available'); return; }
        setLoadingLabel(prev => ({ ...prev, [labelType]: true }));
        try {
            const response = await getOrderLabel(oid, labelType);
            const blob = new Blob([response.data], { type: 'application/pdf' });
            window.open(URL.createObjectURL(blob), '_blank');
            toast.success(`${labelType === 'prepaid' ? 'Prepaid' : 'COD'} label opened`);
        } catch (error) {
            console.error('Label error:', error);
            toast.error(`Failed to generate ${labelType} label`);
        }
        setLoadingLabel(prev => ({ ...prev, [labelType]: false }));
    };

    const handleFieldChange = (field, value) => {
        setEditingOrder(prev => ({ ...prev, [field]: value }));
    };

    const handleItemChange = (index, field, value) => {
        setEditingOrder(prev => {
            const items = [...prev.items];
            items[index] = { ...items[index], [field]: value };
            return { ...prev, items };
        });
    };

    const handleAddItem = () => {
        setEditingOrder(prev => ({
            ...prev,
            items: [...prev.items, { name: '', quantity: 1, note: '' }]
        }));
    };

    const handleRemoveItem = (index) => {
        if (editingOrder.items.length <= 1) {
            toast.error("At least one item is required");
            return;
        }
        setEditingOrder(prev => ({
            ...prev,
            items: prev.items.filter((_, i) => i !== index)
        }));
    };

    const handlePrintB2bLabel = () => {
        const oid = orderDetails?.id || order_id;
        const mappedItems = Array.isArray(orderDetails?.order_items) 
            ? orderDetails.order_items.map(item => ({
                name: item.aka_name || item.product_name || '',
                quantity: item.quantity || 1,
                note: item.note || item.dispatch_note || ''
              }))
            : [{ name: '', quantity: 1, note: '' }];

        setEditingOrder({
            order_id: oid || '',
            customer_name: orderDetails?.user || orderDetails?.name || '',
            shop_name: orderDetails?.shop_name || orderDetails?.shipping_user?.shop_name || '',
            mobile_number: orderDetails?.phone_number || orderDetails?.number || orderDetails?.shipping_user?.phone_number || '',
            alternate_number: orderDetails?.alternate_phone || orderDetails?.shipping_user?.alternate_phone || '',
            transport_name: orderDetails?.shipping_user?.transport_name || '',
            transport_number: orderDetails?.shipping_user?.transport_number || '',
            note: orderDetails?.shipping_user?.note || '',
            dispatch_instruction: '',
            items: mappedItems
        });
        setIsEditorOpen(true);
    };

    const handlePrintB2bLabelSubmit = async (e) => {
        e.preventDefault();
        if (!editingOrder.customer_name?.trim()) {
            toast.error("Customer name is required.");
            return;
        }
        const validItems = editingOrder.items?.filter(item => item.name?.trim());
        if (!validItems || validItems.length === 0) {
            toast.error("At least one item with a valid name is required.");
            return;
        }

        setLoadingB2bLabel(true);
        try {
            const payload = {
                customer_name: editingOrder.customer_name.trim(),
                shop_name: editingOrder.shop_name?.trim() || '',
                mobile_number: editingOrder.mobile_number?.trim() || '',
                alternate_number: editingOrder.alternate_number?.trim() || '',
                transport_name: editingOrder.transport_name?.trim() || '',
                transport_number: editingOrder.transport_number?.trim() || '',
                note: editingOrder.note?.trim() || '',
                dispatch_instruction: editingOrder.dispatch_instruction?.trim() || '',
                items: editingOrder.items
                    .filter(item => item.name?.trim())
                    .map(item => ({
                        name: item.name.trim(),
                        quantity: Number(item.quantity) || 1,
                        note: item.note?.trim() || ''
                    }))
            };
            if (editingOrder.order_id) {
                payload.order_id = editingOrder.order_id;
            }

            const pdfBlob = await generateB2bOrderLabel(payload);
            const url = URL.createObjectURL(pdfBlob);
            window.open(url, '_blank');
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            toast.success("PDF generated successfully");
            setIsEditorOpen(false);
        } catch (error) {
            console.error('B2B Label error:', error);
            toast.error(error?.message || 'Failed to generate B2B Label');
        } finally {
            setLoadingB2bLabel(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary" />
            </div>
        );
    }

    const statusClass = statusColors[orderDetails?.status] || 'bg-gray-100 text-gray-600';
    const shipping = orderDetails?.shipping_user;
    const totalAmount = parseFloat(orderDetails?.total_price) || 0;
    const dueAmount = parseFloat(orderDetails?.remaining_payment) || 0;
    const paidAmount = Math.max(0, totalAmount - dueAmount);
    const shopName = orderDetails?.shop_name || shipping?.shop_name || orderDetails?.shop || null;
    const logisticsList = Array.isArray(orderDetails?.logistic)
        ? orderDetails.logistic
        : Array.isArray(orderDetails?.logistics)
            ? orderDetails.logistics
            : orderDetails?.logistics
                ? [orderDetails.logistics]
                : [];
    const logisticsInfo = logisticsList[0] || {};
    const logisticsPartner = logisticsInfo?.provider || orderDetails?.provider || null;
    const trackingLinks = (() => {
        const links = [];
        const push = (u) => {
            const s = String(u || "").trim();
            if (s && !links.includes(s)) links.push(s);
        };
        push(orderDetails?.tracking_link);
        logisticsList.forEach((l) => push(l?.tracking_link));
        return links;
    })();
    const salesPersonLabel = getSalesPersonDisplay(orderDetails);
    const primaryPhone = String(
        orderDetails?.phone_number ||
        orderDetails?.number ||
        shipping?.phone_number ||
        ""
    ).replace(/[^\d]/g, "");
    const alternatePhone = String(
        orderDetails?.alternate_phone ||
        shipping?.alternate_phone ||
        ""
    ).replace(/[^\d]/g, "");
    const summaryPhoneDisplay = (primaryPhone && alternatePhone && alternatePhone !== primaryPhone)
        ? `${primaryPhone}, ${alternatePhone}`
        : (primaryPhone || alternatePhone || "");
    const whatsappPreviewMessage = buildWhatsappPreviewMessage(orderDetails, shipping, salesPersonLabel);
    const whatsappPhoneNumber = String(
        orderDetails?.phone_number ||
        orderDetails?.number ||
        shipping?.phone_number ||
        ""
    ).replace(/[^\d]/g, "");

    const copyText = (text, successMsg = 'Copied to clipboard!') => {
        if (!text) return;
        navigator.clipboard.writeText(text)
            .then(() => toast.success(successMsg))
            .catch(() => toast.error('Failed to copy'));
    };

    const openWhatsappWithPreview = () => {
        if (!whatsappPhoneNumber) {
            toast.error("Customer phone not available");
            return;
        }
        window.open(
            `https://wa.me/${whatsappPhoneNumber}?text=${encodeURIComponent(whatsappPreviewMessage)}`,
            "_blank"
        );
    };

    return (
        <div className="w-full flex flex-col gap-4">

            {/* ── Header ── */}
            <div className="flex items-start justify-between flex-wrap gap-2">
                <div>
                    <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-0.5">Order</p>
                    <div className="flex items-center gap-2">
                        <h2 className="text-lg font-bold text-gray-900">#{getOrderDisplayNumber(orderDetails)}</h2>
                        <button
                            type="button"
                            onClick={() => copyUuid(orderDetails?.id)}
                            className="text-blue-600 hover:text-blue-800"
                            title="Copy UUID"
                        >
                            <BiCopy size={18} />
                        </button>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={() => setShowPreview((prev) => !prev)}
                        className="px-3 py-1 rounded-full text-xs font-semibold bg-gray-800 text-white hover:bg-black transition-colors"
                    >
                        Preview
                    </button>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${statusClass}`}>
                        {orderDetails?.status?.replace('_', ' ')}
                    </span>
                </div>
            </div>

            {showPreview && (
                <SectionCard title="WhatsApp Preview">
                    <div className="flex flex-col gap-3">
                        <div className="whitespace-pre-wrap break-words text-sm bg-gray-50 border border-gray-200 rounded-lg p-3 text-gray-800 font-mono">
                            {whatsappPreviewMessage.split("\n").map(renderWhatsappPreviewLine)}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <button
                                type="button"
                                onClick={() => copyText(whatsappPreviewMessage, 'Preview copied!')}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm font-medium rounded-lg hover:bg-black transition-colors"
                            >
                                Copy Preview
                            </button>
                            <button
                                type="button"
                                onClick={openWhatsappWithPreview}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition-colors"
                            >
                                Open in WhatsApp
                            </button>
                        </div>
                    </div>
                </SectionCard>
            )}

            {/* ── Order Summary Pills ── */}
            <SectionCard title="Order Summary">
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                    {shopName && <InfoRow label="Shop" value={shopName} />}
                    <InfoRow label="Customer" value={orderDetails?.user} />
                    <InfoRow label="Phone" value={summaryPhoneDisplay} />
                    <InfoRow label="Order Date" value={orderDetails?.order_date} />
                    <InfoRow label="Platform" value={orderDetails?.platform} />
                    <InfoRow label="Sales Person" value={salesPersonLabel} />
                    {isAdmin ? (
                        <div className="flex flex-col gap-0.5">
                            <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">Total Amount</span>
                            {editTotalAmount.editing ? (
                                <div className="flex items-center gap-1">
                                    <span className="text-sm font-semibold text-gray-800">₹</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        autoFocus
                                        value={editTotalAmount.value}
                                        onChange={(e) => setEditTotalAmount(prev => ({ ...prev, value: e.target.value }))}
                                        className="w-28 border border-primary rounded px-1.5 py-0.5 text-sm font-semibold text-gray-800 focus:outline-none focus:ring-1 focus:ring-primary"
                                    />
                                    <button
                                        type="button"
                                        disabled={editTotalAmount.saving}
                                        onClick={async () => {
                                            const val = parseFloat(editTotalAmount.value);
                                            if (isNaN(val) || val < 0) { toast.error('Enter a valid amount'); return; }
                                            setEditTotalAmount(prev => ({ ...prev, saving: true }));
                                            try {
                                                const res = await patchOrder({ id: orderDetails.id, data: { total_price: val } });
                                                if (res.status === 200) {
                                                    toast.success('Total amount updated');
                                                    setEditTotalAmount({ editing: false, value: '', saving: false });
                                                    reloadOrderDetails();
                                                } else {
                                                    toast.error('Failed to update amount');
                                                    setEditTotalAmount(prev => ({ ...prev, saving: false }));
                                                }
                                            } catch {
                                                toast.error('Error updating amount');
                                                setEditTotalAmount(prev => ({ ...prev, saving: false }));
                                            }
                                        }}
                                        className="px-2 py-0.5 bg-primary text-white text-xs rounded font-semibold hover:bg-primary/90 disabled:opacity-60"
                                    >
                                        {editTotalAmount.saving ? '...' : 'Save'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setEditTotalAmount({ editing: false, value: '', saving: false })}
                                        className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded font-semibold hover:bg-gray-200"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            ) : (
                                <div className="flex items-center gap-1.5 group">
                                    <span className="text-sm font-semibold text-gray-800 bg-green-100 px-1.5 py-0.5 rounded">
                                        ₹ {totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                    <button
                                        type="button"
                                        title="Edit Total Amount (Admin only)"
                                        onClick={() => setEditTotalAmount({ editing: true, value: totalAmount.toFixed(2), saving: false })}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536M9 13l6.586-6.586a2 2 0 112.828 2.828L11.828 15.828a4 4 0 01-1.414.828l-3 1 1-3a4 4 0 01.828-1.414z" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <InfoRow label="Total Amount" value={`₹ ${totalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    )}
                    <InfoRow label="Paid Amount" value={`₹ ${paidAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    <InfoRow label="Due Amount" value={`₹ ${dueAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`} />
                    {orderDetails?.customer_gstin && (
                        <InfoRow label="Customer GST" value={orderDetails.customer_gstin} />
                    )}
                    {orderDetails?.discount > 0 && (
                        <InfoRow label="Discount" value={`₹ ${orderDetails.discount}`} />
                    )}
                    {orderDetails?.delivery_charge > 0 && (
                        <InfoRow label="Delivery Charge" value={`₹ ${orderDetails.delivery_charge}`} />
                    )}
                    {totalUsedPoints > 0 && (
                        <InfoRow label="Points Used" value={`-${totalUsedPoints} pts`} />
                    )}
                </div>
            </SectionCard>

            {/* ── Shipping Address ── */}
            {(shipping?.shipping_address || shipping?.shipping_note || shipping?.note || shipping?.transport_name || logisticsPartner || trackingLinks.length > 0) && (
                <SectionCard title="Shipping & Transport">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {shipping?.shipping_address && (
                            <InfoRow label="Address" value={shipping.shipping_address} />
                        )}
                        {shipping?.note && (
                            <InfoRow label="Note" value={shipping.note} />
                        )}
                        {shipping?.shipping_note &&
                            String(shipping.shipping_note).trim() !== String(shipping?.note || "").trim() && (
                                <InfoRow label="Delivery Note" value={shipping.shipping_note} />
                            )}
                        {shipping?.transport_name && (
                            <InfoRow label="Transport Name" value={shipping.transport_name} />
                        )}
                        {shipping?.transport_number && (
                            <InfoRow label="Transport Number" value={shipping.transport_number} />
                        )}
                        {logisticsPartner && (
                            <InfoRow label="Logistics Partner" value={logisticsPartner} />
                        )}
                        {trackingLinks.length > 0 && (
                            <div className="flex flex-col gap-1.5">
                                <span className="text-xs text-gray-400 font-medium uppercase tracking-wide">
                                    Tracking{trackingLinks.length > 1 ? ` (${trackingLinks.length})` : ""}
                                </span>
                                <div className="flex flex-wrap gap-2">
                                    {trackingLinks.map((url, i) => (
                                        <button
                                            key={`${i}-${url.slice(0, 20)}`}
                                            type="button"
                                            onClick={() => copyText(url, 'Tracking link copied!')}
                                            className="inline-flex items-center justify-center p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-blue-600"
                                            title={trackingLinks.length > 1 ? `Copy tracking link ${i + 1}` : 'Copy tracking link'}
                                        >
                                            <BiCopy size={18} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                    {shipping?.transport_image && (
                        <div className="mt-3">
                            <p className="text-xs text-gray-400 font-medium uppercase tracking-wide mb-1">Transport Image</p>
                            <img
                                src={shipping.transport_image}
                                alt="Transport"
                                className="h-24 w-auto rounded-lg border border-gray-200 object-cover"
                            />
                        </div>
                    )}
                </SectionCard>
            )}

            {/* ── Order Items (line items / add — was table column; now only here) ── */}
            {orderDetails?.id && (
                <OrderOrdersPopView item={orderDetails} onOperation={reloadOrderDetails} />
            )}

            {/* ── Reward Points ── */}
            {orderDetails?.reward_points?.length > 0 && (
                <OrderRewardPointPopView item={orderDetails} />
            )}

            {/* ── Return / Cancellation Logs ── */}
            {orderDetails?.id && (() => {
                const isStaff = getUserRole() === 'staff';
                const canAdd = !isStaff && ['RETURNED', 'CANCELLED'].includes(orderDetails.status);
                const logs = orderDetails.return_cancellations;
                if (!canAdd && !logs?.length) return null;
                return (
                    <ReturnCancellationSection
                        orderId={orderDetails.id}
                        logs={logs}
                        onAdded={reloadOrderDetails}
                        canAdd={canAdd}
                    />
                );
            })()}

            {/* ── Invoice + Labels ── */}
            {orderDetails?.order_type !== 'B2B' ? (
                orderDetails?.invoice && (
                    <div className="flex flex-wrap gap-3 pt-1">
                        <a
                            href={orderDetails.invoice.startsWith('http') ? orderDetails.invoice : `${BASE_URL}${orderDetails.invoice}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Invoice
                        </a>
                        <button
                            onClick={() => handlePrintLabel('prepaid')}
                            disabled={loadingLabel.prepaid}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
                        >
                            {loadingLabel.prepaid
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                : <span>📦</span>}
                            Prepaid Label
                        </button>
                        <button
                            onClick={() => handlePrintLabel('cod')}
                            disabled={loadingLabel.cod}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition-colors"
                        >
                            {loadingLabel.cod
                                ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                                : <span>💰</span>}
                            COD Label
                        </button>
                    </div>
                )
            ) : (
                <div className="flex flex-wrap gap-3 pt-1">
                    {orderDetails?.invoice && (
                        <a
                            href={orderDetails.invoice.startsWith('http') ? orderDetails.invoice : `${BASE_URL}${orderDetails.invoice}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                            </svg>
                            View Invoice
                        </a>
                    )}
                    <button
                        onClick={handlePrintB2bLabel}
                        disabled={loadingB2bLabel}
                        className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors"
                    >
                        {loadingB2bLabel
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            : <span>🏷️</span>}
                        B2B Label
                    </button>
                </div>
            )}

            {/* B2B SINGLE LABEL EDITOR MODAL */}
            <ShowModal
                isOpen={isEditorOpen}
                onRequestClose={() => setIsEditorOpen(false)}
                title="Edit B2B Label Details"
                modalWidth="60%"
            >
                <form onSubmit={handlePrintB2bLabelSubmit} className="space-y-6 pb-2 text-left">
                    {/* Basic Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Customer Name <span className="text-red-500">*</span></label>
                            <input 
                                type="text" 
                                value={editingOrder.customer_name} 
                                onChange={(e) => handleFieldChange('customer_name', e.target.value)}
                                placeholder="e.g. Rohit Patel" 
                                required
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Shop Name</label>
                            <input 
                                type="text" 
                                value={editingOrder.shop_name} 
                                onChange={(e) => handleFieldChange('shop_name', e.target.value)}
                                placeholder="e.g. Patel Agro Traders" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Mobile Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.mobile_number} 
                                onChange={(e) => handleFieldChange('mobile_number', e.target.value)}
                                placeholder="e.g. 8823012601" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Alternate Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.alternate_number} 
                                onChange={(e) => handleFieldChange('alternate_number', e.target.value)}
                                placeholder="e.g. 9998887776" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Transport Company Name</label>
                            <input 
                                type="text" 
                                value={editingOrder.transport_name} 
                                onChange={(e) => handleFieldChange('transport_name', e.target.value)}
                                placeholder="e.g. VRL Logistics" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-semibold text-gray-600">Transport Contact Number</label>
                            <input 
                                type="text" 
                                value={editingOrder.transport_number} 
                                onChange={(e) => handleFieldChange('transport_number', e.target.value)}
                                placeholder="e.g. 9876543210" 
                                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                            />
                        </div>
                    </div>

                    {/* Note & Dispatch Instruction */}
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-600">Note (Order Level Note)</label>
                        <input 
                            type="text" 
                            value={editingOrder.note} 
                            onChange={(e) => handleFieldChange('note', e.target.value)}
                            placeholder="e.g. Deliver before 6 PM" 
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent" 
                        />
                    </div>

                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-semibold text-gray-600">Dispatch Instructions (Free text, printed on label)</label>
                        <textarea 
                            value={editingOrder.dispatch_instruction} 
                            onChange={(e) => handleFieldChange('dispatch_instruction', e.target.value)}
                            placeholder="e.g. Pack insulators separately. Verify battery count." 
                            rows={2}
                            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none" 
                        />
                    </div>

                    {/* Items Table */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between border-b pb-2 border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-600 uppercase tracking-wider">Item Details</h3>
                            <button 
                                type="button" 
                                onClick={handleAddItem}
                                className="px-3 py-1.5 bg-purple-50 hover:bg-purple-100 text-purple-700 text-xs font-semibold rounded-lg transition-colors border border-purple-200"
                            >
                                + Add Row
                            </button>
                        </div>

                        {/* Items Header */}
                        <div className="hidden md:grid grid-cols-12 gap-2 bg-gray-50 p-2 rounded-t-lg text-xs font-bold text-gray-500 uppercase tracking-wider">
                            <div className="col-span-6">Item Name</div>
                            <div className="col-span-2 text-center">Qty</div>
                            <div className="col-span-3">Item Packing Note</div>
                            <div className="col-span-1 text-center">✕</div>
                        </div>

                        <div className="space-y-2 max-h-[220px] overflow-y-auto pr-1">
                            {editingOrder.items.map((item, index) => (
                                <div key={index} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-center border border-gray-100 p-2 md:p-0 md:border-0 rounded-lg md:rounded-none">
                                    <div className="col-span-6">
                                        <ProductSearchInput 
                                            value={item.name} 
                                            placeholder="Item Name (search or type)..."
                                            onChange={(text) => handleItemChange(index, 'name', text)}
                                            onSelect={(prod) => handleItemChange(index, 'name', prod.product_name)} 
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <input 
                                            type="number" 
                                            min="1" 
                                            value={item.quantity} 
                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm text-center focus:outline-none focus:ring-2 focus:ring-purple-500" 
                                        />
                                    </div>
                                    <div className="col-span-3">
                                        <input 
                                            type="text" 
                                            value={item.note} 
                                            onChange={(e) => handleItemChange(index, 'note', e.target.value)}
                                            placeholder="e.g. Fragile"
                                            className="w-full px-2 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500" 
                                        />
                                    </div>
                                    <div className="col-span-1 text-center">
                                        {editingOrder.items.length > 1 && (
                                            <button 
                                                type="button" 
                                                onClick={() => handleRemoveItem(index)}
                                                className="text-red-500 hover:text-red-700 font-bold transition-colors text-lg"
                                                aria-label="Remove item row"
                                            >
                                                ✕
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer buttons */}
                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="button"
                            onClick={() => setIsEditorOpen(false)}
                            className="px-5 py-2.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loadingB2bLabel}
                            className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 flex items-center gap-1.5"
                        >
                            {loadingB2bLabel && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
                            Print
                        </button>
                    </div>
                </form>
            </ShowModal>

        </div>
    );
}

export default OrderOrdersDetailsPopView;
